import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/googleMaps";
import { Bike, Clock } from "lucide-react";

// Map for an order on its way: the shop, the customer's door and the rider
// (from GET /orders/{id}/tracking → live). The page refreshes the data; this
// only moves the markers.
// Line icons (lucide shapes) drawn white inside the map pins.
const ICONS = {
  shop: '<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/>',
  home: '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
  bike: '<circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/>',
};
const glyph = (name, x, y, size) =>
  `<g transform="translate(${x - size / 2} ${y - size / 2}) scale(${size / 24})" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${ICONS[name]}</g>`;

function pin(fill, icon) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 40 48"><path d="M20 47c-1-3-17-17-17-28a17 17 0 0 1 34 0c0 11-16 25-17 28z" fill="${fill}" stroke="white" stroke-width="2.5"/>${glyph(icon, 20, 19, 16)}</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}
const RIDER_ICON = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="#1C1917" stroke="white" stroke-width="3"/>${glyph("bike", 22, 22, 22)}</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
})();

function ago(s) {
  if (s == null) return "";
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  return `${m} min ago`;
}

export default function LiveDeliveryMap({ live, riderName }) {
  const el = useRef(null);
  const map = useRef(null);
  const marks = useRef({});
  const fitted = useRef(false);
  const [noMap, setNoMap] = useState(false);
  const [tick, setTick] = useState(0);   // map ready → place the markers

  useEffect(() => {
    let gone = false;
    loadGoogleMaps().then(async (g) => {
      if (gone) return;
      if (!g || !el.current) { setNoMap(true); return; }
      const { Map } = await g.maps.importLibrary("maps");
      await g.maps.importLibrary("marker");
      map.current = new Map(el.current, {
        center: live?.destination || live?.shop || { lat: 19.076, lng: 72.8777 },
        zoom: 14, disableDefaultUI: true, zoomControl: true, gestureHandling: "cooperative",
        clickableIcons: false,
      });
      setTick((n) => n + 1);
    });
    return () => { gone = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const g = window.google;
    if (!map.current || !g || !live) return;
    const place = (key, pos, icon, title, size) => {
      if (!pos) { marks.current[key]?.setMap(null); delete marks.current[key]; return; }
      if (!marks.current[key]) {
        marks.current[key] = new g.maps.Marker({
          map: map.current, position: pos, title,
          icon: { url: icon, scaledSize: new g.maps.Size(size[0], size[1]), anchor: new g.maps.Point(size[0] / 2, key === "rider" ? size[1] / 2 : size[1]) },
          zIndex: key === "rider" ? 3 : 1,
        });
      } else {
        marks.current[key].setPosition(pos);
      }
    };
    place("shop", live.shop, pin("#F59E0B", "shop"), live.shop_name || "Shop", [40, 48]);
    place("home", live.destination, pin("#0F766E", "home"), "Your address", [40, 48]);
    place("rider", live.rider, RIDER_ICON, riderName || "Your rider", [44, 44]);
    if (!fitted.current) {
      const b = new g.maps.LatLngBounds();
      [live.shop, live.destination, live.rider].filter(Boolean).forEach((p) => b.extend(p));
      if (!b.isEmpty()) { map.current.fitBounds(b, 60); fitted.current = true; }
    }
  }, [live, tick, riderName]);

  if (!live) return null;
  const r = live.rider;
  return (
    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white" data-testid="live-map">
      {!noMap && <div ref={el} className="h-72 sm:h-80 w-full bg-stone-100" />}
      <div className="p-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-teal/10 text-teal flex items-center justify-center shrink-0"><Bike className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          {live.eta_minutes != null ? (
            <div className="font-heading text-lg font-semibold" data-testid="live-eta">
              Arriving in about {live.eta_minutes} min{live.distance_km != null ? <span className="text-sm font-normal text-stone-500"> · {live.distance_km} km away</span> : null}
            </div>
          ) : <div className="font-heading text-lg font-semibold">On the way</div>}
          {live.deliveries_before > 0 && (
            <div className="text-xs text-stoneWarm font-semibold" data-testid="live-before">
              {live.deliveries_before === 1 ? "1 delivery before yours" : `${live.deliveries_before} deliveries before yours`} on this trip
            </div>
          )}
          <div className="text-xs text-stone-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {r ? (r.live ? `${riderName || "Rider"}'s location updated ${ago(r.age_seconds)}` : `Rider's location last seen ${ago(r.age_seconds)} — their phone may have lost signal`)
               : "Rider's live location isn't available — the time is an estimate from the shop"}
          </div>
        </div>
      </div>
    </div>
  );
}
